import { CloseRounded, GitHub, LinkedIn, Launch } from "@mui/icons-material";
import { Modal } from "@mui/material";
import React from "react";
import styled, { keyframes } from "styled-components";

// ==== Animations ====
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

// ==== Styled Components ====
const Container = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 40px 12px;
  z-index: 1300;
`;

const Wrapper = styled.div`
  max-width: 850px;
  width: 100%;
  border-radius: 24px;
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text_primary};
  padding: 32px;
  display: flex;
  flex-direction: column;
  position: relative;
  animation: ${fadeInUp} 0.6s ease;
  box-shadow: 0px 10px 35px rgba(0, 0, 0, 0.25),
    inset 0px 0px 0px 1px rgba(255, 255, 255, 0.1);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #854ce6, #5ab2ff, #ff6b6b, #4ecdc4);
    background-size: 400% 400%;
    animation: ${gradientShift} 8s ease infinite;
  }
`;

const CloseButton = styled(CloseRounded)`
  position: absolute;
  top: 16px;
  right: 16px;
  font-size: 32px !important;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  transition: all 0.3s ease;
  padding: 6px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  z-index: 2; /* ensures it's above the image */

  &:hover {
    color: ${({ theme }) => theme.primary};
    transform: rotate(90deg);
    background: rgba(133, 76, 230, 0.1);
  }
`;

const ImageContainer = styled.div`
  position: relative; /* needed for absolute positioning of CloseButton */
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 24px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0px 12px 30px rgba(0, 0, 0, 0.3);
  }
`;

const Image = styled.img`
  width: 100%;
  height: 280px;
  object-fit: cover;
  display: block; /* prevent weird gaps */
  transition: transform 0.5s ease;
  z-index: 1; /* ensure image stays below button */
  position: relative;

  ${ImageContainer}:hover & {
    transform: scale(1.03);
  }
`;

const Title = styled.h2`
  font-size: 32px;
  font-weight: 800;
  margin: 8px 0;
  background: linear-gradient(135deg, #854ce6, #5ab2ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.5px;
`;

const Date = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: "📅";
    font-size: 16px;
  }
`;

const Desc = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.7;
  color: ${({ theme }) => theme.text_primary};
  margin: 16px 0 24px 0;
  padding: 16px;
  background: rgba(133, 76, 230, 0.03);
  border-radius: 12px;
  border-left: 4px solid #854ce6;
`;

const Label = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin: 20px 0 12px 0;
  color: ${({ theme }) => theme.text_primary};
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: "";
    display: block;
    width: 6px;
    height: 20px;
    background: linear-gradient(135deg, #854ce6, #5ab2ff);
    border-radius: 10px;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 24px;
`;

const Tag = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(135deg, #854ce6, #5ab2ff);
  padding: 8px 16px;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(133, 76, 230, 0.25);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(133, 76, 230, 0.4);
  }
`;

const Members = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Member = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  border-radius: 16px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(133, 76, 230, 0.05);
    transform: translateX(4px);
  }
`;

const MemberImage = styled.img`
  width: 52px;
  height: 52px;
  object-fit: cover;
  border-radius: 50%;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  ${Member}:hover & {
    box-shadow: 0px 6px 16px rgba(133, 76, 230, 0.3);
  }
`;

const MemberName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_primary};
  flex: 1;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(133, 76, 230, 0.1);
  color: ${({ theme }) => theme.text_secondary};
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #854ce6, #5ab2ff);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(133, 76, 230, 0.3);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 32px;
  flex-wrap: wrap;
`;

const Button = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 1;
  min-width: 160px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 20px;
  border-radius: 16px;
  background: ${({ dull }) =>
    dull
      ? "rgba(133, 76, 230, 0.1)"
      : "linear-gradient(135deg, #854ce6, #5ab2ff)"};
  color: ${({ dull, theme }) => (dull ? theme.text_secondary : "#fff")};
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: ${({ dull }) =>
    dull ? "none" : "0 6px 16px rgba(133, 76, 230, 0.3)"};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: 0.5s;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ dull }) =>
      dull
        ? "0 6px 16px rgba(0,0,0,0.1)"
        : "0 10px 24px rgba(133, 76, 230, 0.4)"};

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

// ==== Component ====
const ProjectModal = ({ openModal, setOpenModal }) => {
  const project = openModal?.project;

  return (
    <Modal
      open={true}
      onClose={() => setOpenModal({ state: false, project: null })}
    >
      <Container>
        <Wrapper>
          <CloseButton
            onClick={() => setOpenModal({ state: false, project: null })}
          />

          <ImageContainer>
            <Image src={project?.image} alt={project?.title} />
          </ImageContainer>

          <Title>{project?.title}</Title>
          <Date>{project?.date}</Date>

          <Tags>
            {project?.tags.map((tag, idx) => (
              <Tag key={idx}>{tag}</Tag>
            ))}
          </Tags>

          <Desc>{project?.description}</Desc>

          {project?.member && (
            <>
              <Label>Team Members</Label>
              <Members>
                {project.member.map((member, idx) => (
                  <Member key={idx}>
                    <MemberImage src={member.img} alt={member.name} />
                    <MemberName>{member.name}</MemberName>
                    <SocialLink href={member.github} target="new">
                      <GitHub />
                    </SocialLink>
                    <SocialLink href={member.linkedin} target="new">
                      <LinkedIn />
                    </SocialLink>
                  </Member>
                ))}
              </Members>
            </>
          )}

          <ButtonGroup>
            <Button dull href={project?.github} target="new">
              <GitHub /> View Code
            </Button>
            <Button href={project?.webapp} target="new">
              <Launch /> View Live App
            </Button>
          </ButtonGroup>
        </Wrapper>
      </Container>
    </Modal>
  );
};

export default ProjectModal;
